import { FC, ReactNode } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { StorageDocumentLink } from '../../../../components/storage-document-link/index';
import {
  EventSignatureSummary,
  EventTypeBackend,
  EventUnion,
} from '../../../../types/events.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import {
  isDocusignFileLink,
  resolveDisplayedDocusignFileLinks,
  resolveEventAllDocusignFileLinks,
  resolveEventGeneralManualFileLinks,
  resolveEventLevelDocusignFileLinks,
} from '../utils/resolve-linked-signature-file-links.util';
import { EventDocusignDocPreviewLinks } from './event-docusign-doc-preview-links';
import { GrantSummaryPlainFields, GrantSummaryPlainRow } from './grant-event-summary-layout';

const EXERCISE_FAMILY_EVENT_TYPES: EventTypeBackend[] = [
  EventTypeBackend.APPROVAL,
  EventTypeBackend.EXERCISE,
  EventTypeBackend.PAYOUT,
];

const isExerciseFamilyEvent = (
  event: ({ signatureSummary?: EventSignatureSummary } & EventUnion) | null,
): boolean => Boolean(event && EXERCISE_FAMILY_EVENT_TYPES.includes(event.type));

type StepTwoFile = {
  doc?: { name?: string };
  docLink?: string;
};

export type EventAggregateDocumentsPreviewProps = {
  editAction?: ReactNode;
  event: ({ signatureSummary?: EventSignatureSummary } & EventUnion) | null;
  files?: StepTwoFile[];
  filesLinks?: string[];
  onDocumentClick?: (fileLink: string) => void;
  previewMode?: boolean;
};

const ManualDocumentLinks: FC<{
  items: Array<{ label: string; link: string }>;
  onDocumentClick?: (fileLink: string) => void;
}> = ({ items, onDocumentClick }) => {
  if (items.length === 0) {
    return <span className="truncate text-xs font-[450] text-gray-700">-</span>;
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 overflow-hidden">
      {items.map(({ label, link }) => (
        <StorageDocumentLink
          key={link}
          label={label}
          link={link}
          onDocumentClick={onDocumentClick}
        />
      ))}
    </div>
  );
};

export const EventAggregateDocumentsPreview: FC<EventAggregateDocumentsPreviewProps> = ({
  event,
  files,
  filesLinks,
  onDocumentClick,
  previewMode,
  editAction,
}) => {
  const rawFileLinks = files
    ? (files ?? [])
        .map((file) => file.docLink)
        .filter((docLink): docLink is string => Boolean(docLink))
    : (filesLinks ?? []);

  const allEventDocusignCandidates = resolveEventAllDocusignFileLinks(event);
  const usesSharedExerciseFiles = isExerciseFamilyEvent(event);

  const docusignLinks = usesSharedExerciseFiles
    ? allEventDocusignCandidates
    : resolveEventLevelDocusignFileLinks(event);

  const generalManualLinks = usesSharedExerciseFiles
    ? rawFileLinks.filter((link) => !isDocusignFileLink(link, allEventDocusignCandidates))
    : resolveEventGeneralManualFileLinks(event, rawFileLinks);

  const manualItems = generalManualLinks.map((link) => {
    const file = files?.find(
      (entry) =>
        entry.docLink && resolveDisplayedDocusignFileLinks([link], [entry.docLink]).length > 0,
    );

    return {
      label: file?.doc?.name ?? getS3FileOriginalName(link) ?? link,
      link,
    };
  });

  return (
    <GrantSummaryPlainFields>
      <div className="flex w-full items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <GrantSummaryPlainRow
            label={<AppFormattedMessage id="DOCUMENTS" />}
            value={<ManualDocumentLinks items={manualItems} onDocumentClick={onDocumentClick} />}
          />
          {docusignLinks.length > 0 && (
            <div className="border-t border-gray-100">
              <GrantSummaryPlainRow
                label={<AppFormattedMessage id="DOCUSIGN_DOC" />}
                value={
                  <EventDocusignDocPreviewLinks
                    fileLinks={docusignLinks}
                    onDocumentClick={onDocumentClick}
                    previewMode={previewMode}
                  />
                }
              />
            </div>
          )}
        </div>
        {editAction ? <div className="shrink-0 pt-2">{editAction}</div> : null}
      </div>
    </GrantSummaryPlainFields>
  );
};
